import React from "react";
import { motion } from "framer-motion";
import { LayoutGrid, History } from "lucide-react";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

interface LedgerTabHeaderProps {
  activeTab: "Active" | "History";
  setActiveTab: (tab: "Active" | "History") => void;
}

const LedgerTabHeader: React.FC<LedgerTabHeaderProps> = ({ activeTab, setActiveTab }) => {
  const { bgClass, fontClass} = useUserTheme();

  const tabs = [
    { id: "Active", label: "Orders", icon: LayoutGrid },
    { id: "History", label: "History", icon: History },
  ] as const;

  return (
    <div className={`${fontClass} w-full mb-1.5 antialiased`}>
      {/* Container with a slight inner shadow to look recessed */}
      <div className="relative inline-flex p-1.5 bg-slate-200/50 backdrop-blur-2xl rounded-2xl border border-slate-400/30 shadow-inner gap-1.5">
        
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                relative flex items-center gap-4 px-4 py-1.5 rounded-xl text-sm font-bold tracking-tight transition-all duration-300 outline-none group
                ${isActive 
                  ? "text-white" 
                  : "text-slate-900 bg-white/30 hover:bg-white/60 shadow-sm border border-slate-400/50"
                }
              `}
            >
              {/* Kinetic Background Indicator for Active Tab */}
              {isActive && (
                <motion.div
                  layoutId="activeLedgerTab"
                  className={`absolute inset-0 rounded-xl ${bgClass} shadow-[0_4px_12px_rgba(0,0,0,0.12)] ring-1 ring-white/30 z-0`}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                >
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
                </motion.div>
              )}

              {/* Content Label */}
              <div className="relative z-10 flex items-center gap-2">
                <Icon 
                  size={16} 
                  strokeWidth={isActive ? 2.5 : 2}
                  className={`transition-all duration-300 ${isActive ? "scale-110" : "opacity-60 group-hover:opacity-100 group-hover:scale-105"}`} 
                />
                <span>{tab.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LedgerTabHeader;